import { Link } from 'react-router-dom';
import CustomTooltip from '../components/elements/CustomTooltip';
import Label from '../components/elements/Label';
import { getDateAndTime } from '../lib/utils';

export const projectTableHead = [
  {
    key: 'key',
    label: 'Key',
    sortable: true,
    formatContent: (project) => (
      <Link to={`/project/${project.id}/dashboard`}>{project.key}</Link>
    ),
  },
  {
    key: 'name',
    label: 'Name',
    sortable: true,
  },
  {
    key: 'description',
    label: 'Description',
    sortable: false,
    formatContent: (row) => (
      <span title={row?.description} classsummary="ellipsis">
        {row?.description.length > 50
          ? `${row?.description?.substring(0, 50)} ...`
          : row?.description || '-'}
      </span>
    ),
  },
  {
    key: 'linkedProjectName',
    label: 'Linked JIRA Project',
    sortable: false,
    formatContent: (project) =>
      project?.linked_jira_projects?.length > 0
        ? project.linked_jira_projects.map(p => p.linked_project_name).join(', ')
        : 'N/A',
  },
  {
    key: 'created_at',
    label: 'Created On',
    sortable: true,
    formatContent: (project) => getDateAndTime(project.created_at),
  },
];

export const customerDataHeaders = [
  {
    key: 's_no',
    label: 'S.No.',
    // sortable: true,
    formatContent: (row, index, page, perPage) => (page - 1) * perPage + index + 1,
  },
  {
    key: 'customer',
    label: 'Customer',
    sortable: true,
  },
  {
    key: 'type',
    label: 'Type',
    sortable: false,
    formatContent: (row) => row?.type || 'N/A',
  },
  {
    key: 'org_admin',
    label: 'Org Admin',
    sortable: false,
    formatContent: (row) => {
      const { email, phone_number, full_name } = row?.org_admin || {};

      return (
        <CustomTooltip
          tooltipData={{
            Email: email,
            Phone: phone_number ? `+${phone_number}` : null,
          }}>
          {full_name}
        </CustomTooltip>
      );
    },
  },
  {
    key: 'statusName',
    label: 'Status',
    sortable: false,
    formatContent: (row) => (
      <span
        title={row?.status}
        style={{
          display: 'flex',
          alignItems: 'center',
        }}>
        <Label name={row?.status} />
      </span>
    ),
  },
  {
    key: 'number_of_projects',
    label: '# of Projects',
    sortable: true,
    formatContent: (row) => row.number_of_projects || 0,
  },
  {
    key: 'number_of_users',
    label: '# of Users',
    sortable: false,
    formatContent: (row) => row.number_of_users || 0,
  },
  {
    key: 'number_of_test_cases',
    label: '# of Test Cases',
    sortable: false,
    formatContent: (row) => row.number_of_test_cases || 0,
  },
  {
    key: 'created_at',
    label: 'Created On',
    sortable: true,
    formatContent: (row) => getDateAndTime(row.created_at),
  },
];

export const deletedCustomersDataHeaders = [
  {
    key: 'name',
    label: 'Customer',
    sortable: true,
  },
  {
    key: 'org_admin',
    label: 'Org Admin',
    sortable: false,
    formatContent: (row) => {
      const { email, phone_number, admin_name } = row || {};
      return (
        <CustomTooltip
          tooltipData={{
            Email: email,
            Phone: phone_number ? `+${phone_number}` : null,
          }}>
          {admin_name}
        </CustomTooltip>
      );
    },
  },
  {
    key: 'status',
    label: 'Status',
    sortable: false,
    formatContent: (row) => (
      <span
        title={row?.status}
        style={{
          display: 'flex',
          alignItems: 'center',
        }}>
        <Label name={row?.status} />
      </span>
    ),
  },
  {
    key: 'account_created_at',
    label: 'Created On',
    sortable: true,
    formatContent: (row) => getDateAndTime(row.account_created_at),
  },
  {
    key: 'deleted_at',
    label: 'Deleted On',
    sortable: true,
    formatContent: (row) => getDateAndTime(row.deleted_at),
  },
];



export const SIMPLE_ENTITY_LABELS = {
  LABEL: 'label',
  TEST_CASE_PRIORITY: 'priority',
  FUNCTIONAL_MODULE_FIELD: 'module',
  PURPOSE: 'test cycle purpose',
  TECHNOLOGY_LAYER: 'technology layer',
  TEST_CASE_STATUS: 'test case status',
  TEST_CYCLE_STATUS: 'test cycle status',
  TEST_EXECUTION_STATUS: 'test execution status',
  ENVIRONMENT: 'environment',
};

export const SIMPLE_ENTITIES = Object.keys(SIMPLE_ENTITY_LABELS);

export const buildHistoryHeader = (entry) => {
  const { entity_name, action_type, changes } = entry;
  const actionText =
    action_type === 'UPDATE' ? 'updated' :
    action_type === 'CREATE' ? 'created' : 'deleted';

  if (entity_name === 'PROJECT_FIELDS_CONFIG') {
    const parentIdFromUpdate = changes?.name?.parent_id;
    const parentIdFromCreateDelete = changes?.parent_id;
    const hasParent =
      (parentIdFromUpdate != null && parentIdFromUpdate !== '') ||
      (parentIdFromCreateDelete != null && parentIdFromCreateDelete !== '');
    const fieldType = hasParent ? 'subcategory' : 'category';
    const parentName = changes?.parent_name;

    if (action_type === 'UPDATE') return `updated ${fieldType}`;

    const name =
      typeof changes?.name === 'object'
        ? changes?.name?.new_value || changes?.name?.old_value
        : changes?.name;

    if (fieldType === 'subcategory' && parentName) {
      return `${actionText} subcategory "${name}" under "${parentName}"`;
    }
    return `${actionText} ${fieldType}${name ? ` "${name}"` : ''}`;
  }

  if (SIMPLE_ENTITIES.includes(entity_name)) {
    const entityLabel = SIMPLE_ENTITY_LABELS[entity_name];
    const name =
      typeof changes?.name === 'object'
        ? changes?.name?.new_value || changes?.name?.old_value
        : changes?.name;

    if (action_type === 'CREATE') {
      const description = changes?.description;
      const descSuffix = description ? ` with description "${description}"` : '';
      return `created ${entityLabel}${name ? ` "${name}"` : ''}${descSuffix}`;
    }

    if (action_type === 'DELETE') {
      return `deleted ${entityLabel}${name ? ` "${name}"` : ''}`;
    }

    const oldName = changes?.name?.old_value;
    const newName = changes?.name?.new_value;
    if (oldName && newName) return `updated ${entityLabel} from "${oldName}" to "${newName}"`;
    return `updated ${entityLabel}${name ? ` "${name}"` : ''}`;
  }

  if (entity_name === 'TestSuite') {
    if (action_type === 'CREATE') return 'created this test suite';
    if (action_type === 'DELETE') return 'deleted this test suite';
    return 'updated this test suite';
  }

  return `${actionText} this project`;
};